import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { toast } from "sonner";
import { useModuleAccess } from "../contexts/ModuleAccessContext";

export default function RoleConfigPanel({ onChanged }) {
  const { refresh: refreshAccess } = useModuleAccess();
  const [catalogue, setCatalogue] = useState(null);
  const [selected, setSelected] = useState("");
  const [modules, setModules] = useState({});
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load(preferred = selected) {
    try {
      const { data } = await axios.get(`${API}/settings/roles`, { withCredentials: true });
      setCatalogue(data);
      const next = data.roles.find(r => r.value === preferred) || data.roles[0];
      setSelected(next?.value || "");
      setModules(next?.modules || {});
      setError("");
      onChanged?.(data.roles);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo cargar la configuración de roles");
    }
  }
  useEffect(() => { load(); }, []); // Carga al abrir Usuarios; no polling.

  async function run(action, message, preferred) {
    setBusy(true);
    setError("");
    try {
      await action();
      toast.success(message);
      await load(preferred);
      refreshAccess();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar el cambio");
    } finally { setBusy(false); }
  }

  const role = catalogue?.roles.find(r => r.value === selected);
  const protectedModules = selected === "Superadministrador";
  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="role-config-panel">
      <h2 className="font-semibold text-gray-900">Configurar roles y acceso a módulos</h2>
      <form className="flex flex-wrap gap-2" onSubmit={e => {
        e.preventDefault();
        const value = name.trim();
        if (!value) return;
        run(() => axios.post(`${API}/settings/roles`, { name: value }, { withCredentials: true }), "Rol creado", value);
      }}>
        <label className="sr-only" htmlFor="new-role-name">Nombre del nuevo rol</label>
        <input id="new-role-name" value={name} maxLength={80} onChange={e => setName(e.target.value)}
          placeholder="Nombre del nuevo rol" disabled={busy} className="border rounded-lg px-3 py-2 text-sm" />
        <button disabled={busy || !name.trim()} className="bg-[#2E6DA4] text-white px-3 py-2 rounded-lg disabled:opacity-50">Agregar rol</button>
      </form>
      {error && <p role="alert" className="text-red-700 text-sm">{error}</p>}
      {!catalogue ? <p className="text-sm text-gray-500">Cargando roles...</p> : <>
        <label className="block text-sm font-medium" htmlFor="configured-role">Rol a configurar</label>
        <select id="configured-role" value={selected} disabled={busy} className="border rounded-lg px-3 py-2 w-full"
          onChange={e => {
            setSelected(e.target.value);
            setModules(catalogue.roles.find(r => r.value === e.target.value)?.modules || {});
          }}>
          {catalogue.roles.map(r => <option key={r.value} value={r.value}>{r.label}{r.protected ? " (rol del sistema)" : ""}</option>)}
        </select>
        <p className="text-sm text-gray-500">{role?.custom ? "Este rol permite consultar los módulos habilitados. No puede crear, editar, eliminar, aprobar ni generar informes o exportaciones." : "El acceso al módulo conserva los permisos de cada operación. Las aprobaciones y acciones de administración mantienen sus controles."}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" aria-label="Acceso por módulo">
          {catalogue.modules.map(module => <label key={module.id} className="flex gap-2 items-center border rounded-lg px-3 py-2 text-sm">
            <input type="checkbox" checked={modules[module.id] === true}
              disabled={busy || protectedModules || !role?.editable || role?.locked_modules?.includes(module.id) || (role?.custom && ["usuarios", "auditoria", "configuracion"].includes(module.id)) || (module.id === "usuarios" && ["Administrador", "Director"].includes(selected)) || (module.id === "auditoria" && selected !== "Superadministrador")}
              onChange={e => setModules(current => ({ ...current, [module.id]: e.target.checked }))} />
            {module.label}
          </label>)}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy || protectedModules || !role?.editable}
            className="bg-[#2E6DA4] text-white px-3 py-2 rounded-lg disabled:opacity-50"
            onClick={() => run(() => axios.put(`${API}/settings/roles/${encodeURIComponent(selected)}/modules`, { modules }, { withCredentials: true }), "Acceso actualizado", selected)}>
            Guardar acceso
          </button>
          {role?.custom && <button type="button" disabled={busy} className="border border-red-300 text-red-700 px-3 py-2 rounded-lg disabled:opacity-50"
            onClick={() => {
              if (!window.confirm(`¿Eliminar el rol ${selected}? Solo se permite si no tiene usuarios asignados.`)) return;
              run(() => axios.delete(`${API}/settings/roles/${encodeURIComponent(selected)}`, { withCredentials: true }), "Rol eliminado", "");
            }}>Eliminar rol</button>}
        </div>
      </>}
    </section>
  );
}
